/**
 * Custom discount logic for 30% off
 * Updates cart drawer prices dynamically when quantity changes
 */

(function() {
  'use strict';

  // Format money using Shopify's formatMoney function if available
  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents, 10);
    }
    
    // Use Shopify's formatMoney if available
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney !== 'undefined') {
      return Shopify.formatMoney(cents);
    }
    
    // Fallback to simple formatting
    const value = cents / 100;
    return '$' + value.toFixed(2);
  }

  // Calculate 30% discount
  function calculateDiscount(price) {
    return Math.round(price * 0.7);
  }

  // Update line item price (works for both cart drawer and main cart)
  function updateLineItemPrice(itemElement, quantity, isMainCart) {
    // Check if this is a bundle product
    const isBundle = isBundleProduct(itemElement, isMainCart);
    
    let priceContainer;
    if (isMainCart) {
      // Main cart: price is in .product-subtotal .price
      priceContainer = itemElement.querySelector('.product-subtotal .price');
    } else {
      // Cart drawer: price is in .product-cart-item-price .price
      priceContainer = itemElement.querySelector('.product-cart-item-price .price');
    }
    
    if (!priceContainer) return;

    const originalPriceElement = priceContainer.querySelector('[data-original-price]');
    const discountedPriceElement = priceContainer.querySelector('[data-discounted-price]');
    
    if (!originalPriceElement) {
      return;
    }

    const currentOriginalPrice = parseInt(originalPriceElement.getAttribute('data-original-price'), 10);
    const currentQuantity = parseInt(itemElement.querySelector('.qty')?.value || 1, 10);
    const unitPrice = currentQuantity > 0 ? currentOriginalPrice / currentQuantity : currentOriginalPrice;
    const newOriginalPrice = Math.round(unitPrice * quantity);
    
    // Skip discount for bundle products
    const newDiscountedPrice = isBundle ? newOriginalPrice : calculateDiscount(newOriginalPrice);

    // Update data attributes
    originalPriceElement.setAttribute('data-original-price', newOriginalPrice);
    if (discountedPriceElement) {
      discountedPriceElement.setAttribute('data-discounted-price', newDiscountedPrice);
      // Update displayed prices
      originalPriceElement.textContent = formatMoney(newOriginalPrice);
      discountedPriceElement.textContent = formatMoney(newDiscountedPrice);
    } else {
      // For bundle products without discount element, just update original price
      originalPriceElement.textContent = formatMoney(newOriginalPrice);
    }
  }

  // Check if product is a bundle or subscription (should be excluded from discount)
  function isBundleProduct(itemElement, isMainCart) {
    let productLink;
    if (isMainCart) {
      productLink = itemElement.querySelector('.cart-product-link');
    } else {
      productLink = itemElement.querySelector('.cart-product-link');
    }
    
    if (!productLink) return false;
    
    const productTitle = productLink.textContent || productLink.innerText || '';
    const productTitleLower = productTitle.toLowerCase();
    
    // Check if product title contains "bundle" or "subscription"
    if (productTitleLower.includes('bundle') || productTitleLower.includes('subscription')) {
      return true;
    }
    
    // Check if item has a selling plan allocation (subscriptions)
    const sellingPlanElement = itemElement.querySelector('.selling-plan');
    if (sellingPlanElement) {
      return true;
    }
    
    return false;
  }

  // Update cart total (works for both cart drawer and main cart)
  function updateCartTotal(isMainCart) {
    let container, lineItems, originalTotalSelector, discountedTotalSelector, checkoutTotalSelector;
    
    if (isMainCart) {
      container = document.getElementById('Cart');
      if (!container) return;
      lineItems = container.querySelectorAll('tbody tr[id^="CartItem-"]');
      originalTotalSelector = '.cart-subtotal .original-total';
      discountedTotalSelector = '.cart-discount .discounted-total';
      checkoutTotalSelector = '.checkout-total-price';
    } else {
      container = document.getElementById('Cart-Drawer');
      if (!container) return;
      lineItems = container.querySelectorAll('.product-cart-item');
      originalTotalSelector = '.original-total';
      discountedTotalSelector = '.discounted-total';
      checkoutTotalSelector = '.checkout-total-price';
    }

    let totalOriginalPrice = 0;
    let bundleTotal = 0;
    let nonBundleTotal = 0;

    lineItems.forEach(item => {
      const isBundle = isBundleProduct(item, isMainCart);
      let originalPriceElement;
      if (isMainCart) {
        originalPriceElement = item.querySelector('.product-subtotal [data-original-price]');
      } else {
        originalPriceElement = item.querySelector('[data-original-price]');
      }
      
      if (originalPriceElement) {
        const originalPrice = parseInt(originalPriceElement.getAttribute('data-original-price'), 10);
        totalOriginalPrice += originalPrice;
        
        if (isBundle) {
          bundleTotal += originalPrice;
        } else {
          nonBundleTotal += originalPrice;
        }
      }
    });

    // Apply discount only to non-bundle items
    const discountedNonBundleTotal = calculateDiscount(nonBundleTotal);
    const totalDiscountedPrice = discountedNonBundleTotal + bundleTotal;

    // Update cart total display
    const originalTotalElement = container.querySelector(originalTotalSelector);
    const discountedTotalElement = container.querySelector(discountedTotalSelector);
    const checkoutTotalElement = container.querySelector(checkoutTotalSelector);

    if (originalTotalElement) {
      originalTotalElement.setAttribute('data-original-total', totalOriginalPrice);
      originalTotalElement.textContent = formatMoney(totalOriginalPrice);
    }
    
    if (discountedTotalElement) {
      discountedTotalElement.setAttribute('data-discounted-total', totalDiscountedPrice);
      discountedTotalElement.textContent = formatMoney(totalDiscountedPrice);
    }

    if (checkoutTotalElement) {
      checkoutTotalElement.textContent = formatMoney(totalDiscountedPrice);
    }
  }

  // Initialize discount updates for cart drawer
  function initCartDrawerUpdates() {
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (!cartDrawer) return;

    // Listen for quantity changes
    cartDrawer.addEventListener('change', function(e) {
      if (e.target.classList.contains('qty')) {
        const itemElement = e.target.closest('.product-cart-item');
        if (itemElement) {
          const quantity = parseInt(e.target.value, 10);
          updateLineItemPrice(itemElement, quantity, false);
          
          // Update cart total after a short delay to allow Shopify to update
          setTimeout(function() { updateCartTotal(false); }, 100);
        }
      }
    });

    // Listen for cart refresh events
    document.addEventListener('cart:refresh', function() {
      setTimeout(function() {
        updateCartTotal(false);
        // Re-initialize all line items
        const lineItems = cartDrawer.querySelectorAll('.product-cart-item');
        lineItems.forEach(function(item) {
          const qtyInput = item.querySelector('.qty');
          if (qtyInput) {
            const quantity = parseInt(qtyInput.value, 10);
            updateLineItemPrice(item, quantity, false);
          }
        });
      }, 300);
    });

    // Listen for line item change events
    document.addEventListener('line-item:change:end', function() {
      setTimeout(function() {
        updateCartTotal(false);
        // Re-initialize all line items
        const lineItems = cartDrawer.querySelectorAll('.product-cart-item');
        lineItems.forEach(function(item) {
          const qtyInput = item.querySelector('.qty');
          if (qtyInput) {
            const quantity = parseInt(qtyInput.value, 10);
            updateLineItemPrice(item, quantity, false);
          }
        });
      }, 300);
    });

    // Initial update
    updateCartTotal(false);
  }

  // Initialize discount updates for main cart page
  function initMainCartUpdates() {
    const mainCart = document.getElementById('Cart');
    if (!mainCart) return;

    // Listen for quantity changes
    mainCart.addEventListener('change', function(e) {
      if (e.target.classList.contains('qty')) {
        const itemElement = e.target.closest('tr[id^="CartItem-"]');
        if (itemElement) {
          const quantity = parseInt(e.target.value, 10);
          updateLineItemPrice(itemElement, quantity, true);
          
          // Update cart total after a short delay to allow Shopify to update
          setTimeout(function() { updateCartTotal(true); }, 100);
        }
      }
    });

    // Listen for cart refresh events
    document.addEventListener('cart:refresh', function() {
      setTimeout(function() {
        updateCartTotal(true);
        // Re-initialize all line items
        const lineItems = mainCart.querySelectorAll('tbody tr[id^="CartItem-"]');
        lineItems.forEach(function(item) {
          const qtyInput = item.querySelector('.qty');
          if (qtyInput) {
            const quantity = parseInt(qtyInput.value, 10);
            updateLineItemPrice(item, quantity, true);
          }
        });
      }, 300);
    });

    // Initial update
    updateCartTotal(true);
  }

  // Initialize discount updates
  function initDiscountUpdates() {
    initCartDrawerUpdates();
    initMainCartUpdates();
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDiscountUpdates);
  } else {
    initDiscountUpdates();
  }

  // Re-initialize when cart content is updated
  function setupObserver() {
    const cartDrawer = document.getElementById('Cart-Drawer');
    const mainCart = document.getElementById('Cart');

    function createObserver(container, isMainCart) {
      if (!container) return;

      const observer = new MutationObserver(function(mutations) {
        let shouldUpdate = false;
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length || mutation.removedNodes.length) {
            shouldUpdate = true;
          }
        });
        
        if (shouldUpdate) {
          setTimeout(function() {
            updateCartTotal(isMainCart);
            // Re-initialize all line items
            let lineItems;
            if (isMainCart) {
              lineItems = container.querySelectorAll('tbody tr[id^="CartItem-"]');
            } else {
              lineItems = container.querySelectorAll('.product-cart-item');
            }
            
            lineItems.forEach(function(item) {
              const qtyInput = item.querySelector('.qty');
              if (qtyInput) {
                const quantity = parseInt(qtyInput.value, 10);
                updateLineItemPrice(item, quantity, isMainCart);
              }
            });
          }, 200);
        }
      });

      observer.observe(container, {
        childList: true,
        subtree: true
      });
    }

    if (cartDrawer) {
      createObserver(cartDrawer, false);
    }
    if (mainCart) {
      createObserver(mainCart, true);
    }
  }

  // Setup observer after initial load
  setTimeout(setupObserver, 500);

})();

